import React, { useMemo, useState } from 'react';
import { useGetRequest, usePutRequest } from '@/utils/useStatefulRequests';
import { ListResponse } from '@/wpt-lib/interfaces/listResponse';
import { Faculty } from '@/wpt-lib/interfaces/Faculty';
import { Organisation } from '@/wpt-lib/interfaces/Organisation';
import { Button, FormLabel, HStack, Switch, Text, VStack } from '@chakra-ui/react';
import { LabeledSelect } from '@/components/FormInputField';
import StateDisplay from '@/components/StateDisplay/StateDisplay';
import { SimpleAlert } from '@/components/SimpleAlert/SimpleAlert';

import { useAssignment } from '@/components/contexts/AssignmentContext/useAssignment';
import { useAsyncEffect } from '@/utils/hooks/useAsyncEffect';
import { isNullish } from '@/wpt-lib/utils/isNullish';

import OrganisationEditorTable from '@/components/pages/Admin/Faculty/OrganisationEditorTable';

interface UpdateOrganisationDto {
    updates: {
        id: number;
        inactive?: boolean;
        workdayAltHierarchyId?: string | null;
    }[];
}

const OrganisationEditor = () => {
    const { currentAssignment } = useAssignment();

    const {
        error: facultiesError,
        loading: facultiesLoading,
        responseData: facultiesResponse,
        makeRequest: loadFaculties,
    } = useGetRequest<ListResponse<Faculty>>();

    const {
        error: organisationsError,
        loading: organisationsLoading,
        responseData: organisationsResponse,
        makeRequest: loadOrganisations,
    } = useGetRequest<Organisation[]>();

    const {
        error: saveError,
        loading: saveLoading,
        makeRequest: save,
    } = usePutRequest<Organisation[], UpdateOrganisationDto>();

    const [facultyId, setFacultyId] = useState<number | undefined>(undefined);
    const [updated, setUpdated] = useState<Organisation[]>([]);
    const [showInactive, setShowInactive] = useState(false);

    useAsyncEffect(async () => {
        try {
            await loadFaculties(`faculty`, { params: { all: true } });
        } catch (e) {
            // handled by hook
        }
    }, []);

    const fetchOrganisations = async (id: number) => {
        try {
            await loadOrganisations(`faculty/${id}/organisation`, {
                params: {
                    assignment: currentAssignment?.id,
                    all: true,
                },
            });
        } catch (e) {
            // handled by hook
        }
    };

    const faculties = useMemo(() => {
        const list = facultiesResponse?.items ?? [];
        return [...list].sort((a, b) => a.name.localeCompare(b.name));
    }, [facultiesResponse]);

    const onSelectFaculty = async (id: number | undefined) => {
        setFacultyId(id);
        setUpdated([]);
        if (!isNullish(id)) {
            await fetchOrganisations(id);
        }
    };

    const onUpdateOrganisation = (organisation: Organisation) => {
        setUpdated([...(updated || []).filter((o) => o.id !== organisation.id), organisation]);
    };

    const handleSave = async () => {
        if (updated.length === 0 || isNullish(facultyId)) return;

        await save('organisation', { updates: updated }, { params: { assignment: currentAssignment?.id } });
        setUpdated([]);
        await fetchOrganisations(facultyId);
    };

    const organisations = useMemo(() => {
        const list =
            organisationsResponse?.map((organisation) => ({
                ...organisation,
                ...(updated.find((updatedOrg) => updatedOrg.id === organisation.id) ?? {}),
                changed: updated.some((updatedOrg) => updatedOrg.id === organisation.id),
            })) ?? [];

        list.sort((a, b) => a.name.localeCompare(b.name));
        return showInactive ? list : list.filter((o) => !o.inactive || o.changed);
    }, [organisationsResponse, updated, showInactive]);

    return (
        <VStack alignItems={'flex-start'} spacing={3} w="full">
            <Text>
                This section allows you to mark organisation units active or inactive. Select a faculty to display its
                organisation units.
            </Text>
            <StateDisplay
                data={faculties}
                loading={facultiesLoading}
                error={!!facultiesError}
                errorRender={() => <SimpleAlert variant="subtle">Could not load faculty list</SimpleAlert>}
                render={() => (
                    <LabeledSelect
                        label="Faculty:"
                        labelProps={{ fontWeight: 'bold' }}
                        placeholder="Select a faculty"
                        value={facultyId ?? ''}
                        onChange={(e) =>
                            onSelectFaculty(e.target.value === '' ? undefined : parseInt(e.target.value, 10))
                        }
                        minW="240px"
                        width="fit-content"
                        controlProps={{ width: 'inherit' }}
                    >
                        {faculties.map((faculty) => (
                            <option value={faculty.id} key={faculty.id}>
                                {faculty.label ?? faculty.name}
                            </option>
                        ))}
                    </LabeledSelect>
                )}
            />

            {!isNullish(facultyId) && (
                <StateDisplay
                    data={organisations}
                    loading={organisationsLoading}
                    error={!!organisationsError}
                    errorRender={() => <SimpleAlert variant="subtle">Could not load organisation list</SimpleAlert>}
                    render={() => (
                        <VStack w="100%" alignItems={'flex-end'}>
                            <HStack mt={-8}>
                                <FormLabel htmlFor="show-inactive-organisations" mb={0}>
                                    Show all organisation units
                                </FormLabel>
                                <Switch
                                    id="show-inactive-organisations"
                                    isChecked={showInactive}
                                    onChange={() => setShowInactive((v) => !v)}
                                    colorScheme="blue"
                                />
                            </HStack>
                            <OrganisationEditorTable organisations={organisations} onUpdate={onUpdateOrganisation} />
                            <HStack>
                                {updated.length > 0 && (
                                    <Text>
                                        {updated.length} organisation unit{updated.length === 1 ? '' : 's'} with pending
                                        changes
                                    </Text>
                                )}
                                {!isNullish(saveError) && (
                                    <Text>Error saving changes, please refresh page and try again.</Text>
                                )}
                                <Button isDisabled={updated.length === 0 || saveLoading} onClick={() => setUpdated([])}>
                                    Reset
                                </Button>
                                <Button
                                    isDisabled={updated.length === 0}
                                    isLoading={saveLoading}
                                    onClick={handleSave}
                                    colorScheme="blue"
                                >
                                    Save
                                </Button>
                            </HStack>
                        </VStack>
                    )}
                />
            )}
        </VStack>
    );
};

export default OrganisationEditor;
